import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environment';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import type { Priority } from '@sharedlib/common/types/tasking-app/priority';
import { type Observable } from 'rxjs';
import { type Project } from '../types/project';

export interface ProjectVersion {
  readonly version: number;
  readonly kind: 'created' | 'baseline' | 'edited' | 'restored';
  readonly author: { readonly id: string; readonly name: string | null } | null;
  readonly at: string;
  /** The fields this save changed, by their stored names (title, description, color, priority, …). */
  readonly fields: readonly string[];
  readonly restoredFrom: number | null;
  readonly review: 'requested' | 'approved' | 'changes_requested' | null;
}
export interface ProjectVersionsPage {
  readonly items: readonly ProjectVersion[];
  readonly current: number | null;
  readonly total: number;
}
/** One saved version with the project's content as it was saved then. */
export interface SavedProjectVersion extends ProjectVersion {
  readonly title: string | null;
  readonly description: string | null;
  readonly color: string | null;
  readonly priority: Priority | null;
  readonly startDateTime: string | null;
  readonly endDateTime: string | null;
  readonly hasStartTime: boolean | null;
  readonly hasEndTime: boolean | null;
}
/** `project` as PUT returns it; `restored: false` changed nothing. */
export interface ProjectRestoreAnswer {
  readonly project: Partial<Project>;
  readonly version: number;
  readonly restored: boolean;
}

/**
 * tasking-app's version routes (#2079), always under the project's own workspace. Provided per component, never at root: only
 * the space-context subtree's HttpClient carries the spaces interceptor that turns the owning-tenant context into the
 * request's space_id; the root client would send none (#2074).
 */
@Injectable()
export class ProjectVersionsClient {
  private readonly http = inject(HttpClient);

  public list(project: { id: string; accountId: string }, offset: number): Observable<ProjectVersionsPage> {
    return this.http.get<ProjectVersionsPage>(this.url(project), {
      params: new HttpParams().set('offset', offset).set('limit', 25),
      context: owningTenantContext(project.accountId),
    });
  }
  public read(project: { id: string; accountId: string }, version: number): Observable<SavedProjectVersion> {
    return this.http.get<SavedProjectVersion>(`${this.url(project)}/${version}`, {
      context: owningTenantContext(project.accountId),
    });
  }
  public restore(project: { id: string; accountId: string }, version: number): Observable<ProjectRestoreAnswer> {
    return this.http.post<ProjectRestoreAnswer>(`${this.url(project)}/${version}/restore`, null, {
      context: owningTenantContext(project.accountId),
    });
  }
  private url(project: { id: string }): string {
    return `${environment.baseUrl}/api/tasking/projects/${encodeURIComponent(project.id)}/versions`;
  }
}
